/* eslint-disable complexity */
import { SunStatus } from '@app/app/analysis/sat-math';
import { Pickable } from '@app/engine/core/interfaces';
import { EventBus } from '@app/engine/events/event-bus';
import { EventBusEvent } from '@app/engine/events/event-bus-events';
import { html } from '@app/engine/utils/development/formatter';
import { hideEl } from '@app/engine/utils/get-el';
import { waitForCruncher } from '@app/engine/utils/waitForCruncher';
import { CruncerMessageTypes } from '@app/webworker/positionCruncher';
import { ColorScheme } from './color-scheme';
import { ServiceLocator } from '@app/engine/core/service-locator';
export class SunlightColorScheme extends ColorScheme {
    label = 'Sunlight Status';
    id = 'SunlightColorScheme';
    static id = 'SunlightColorScheme';
    static uniqueObjectTypeFlags = {
        satHi: true,
        satMed: true,
        satLow: true,
        sunlightFov: true,
        sensor: true,
    };
    static uniqueColorTheme = {
        sunlight100: [0.0, 0.0, 0.0, 1.0],
        sunlight80: [0.0, 0.0, 0.0, 1.0],
        sunlight60: [0.0, 0.0, 0.0, 1.0],
        sunlightInview: [0.0, 0.0, 0.0, 1.0],
        umbral: [0.0, 0.0, 0.0, 1.0],
        penumbral: [0.0, 0.0, 0.0, 1.0],
    };
    constructor() {
        super(SunlightColorScheme.uniqueColorTheme);
        this.objectTypeFlags = {
            ...this.objectTypeFlags, ...SunlightColorScheme.uniqueObjectTypeFlags,
        };
        EventBus.getInstance().on(EventBusEvent.onKeepTrackReady, () => {
            const catalogManagerInstance = ServiceLocator.getCatalogManager();
            const colorSchemeManagerInstance = ServiceLocator.getColorSchemeManager();
            if (colorSchemeManagerInstance.currentColorScheme === this) {
                catalogManagerInstance.satCruncher.postMessage({
                    isSunlightView: true,
                    typ: CruncerMessageTypes.SUNLIGHT_VIEW,
                });
            }
        });
        EventBus.getInstance().on(EventBusEvent.layerUpdated, () => {
            if (settingsManager.isDisableSensors) {
                this.objectTypeFlags.sunlightFov = false;
                const sunlightFovBox = document.querySelector('.layers-sunlightFov-box')?.parentElement;
                if (sunlightFovBox) {
                    hideEl(sunlightFovBox);
                }
            }
        });
    }
    onSelected() {
        const catalogManagerInstance = ServiceLocator.getCatalogManager();
        const colorSchemeManagerInstance = ServiceLocator.getColorSchemeManager();
        catalogManagerInstance.satCruncher.postMessage({
            isSunlightView: true,
            typ: CruncerMessageTypes.SUNLIGHT_VIEW,
        });
        waitForCruncher({
            cruncher: catalogManagerInstance.satCruncher,
            cb: () => {
                colorSchemeManagerInstance.setColorScheme(this, true);
            },
            validationFunc: (data) => data.satInSun,
        });
    }
    update(obj) {
        const dotsManagerInstance = ServiceLocator.getDotsManager();
        if ((dotsManagerInstance.inSunData?.length ?? -1) < obj.id) {
            return {
                color: this.colorTheme.deselected,
                pickable: Pickable.No,
            };
        }
        const checkFacility = this.checkFacility_(obj);
        if (checkFacility) {
            return checkFacility;
        }
        if (obj.isStar()) {
            return this.starColor_(obj);
        }
        if (obj.isMarker()) {
            return this.getMarkerColor_();
        }
        if (obj.isSensor()) {
            if (this.objectTypeFlags.sensor === false) {
                return {
                    color: this.colorTheme.deselected,
                    pickable: Pickable.No,
                };
            }
            return {
                color: this.colorTheme.sensor,
                pickable: Pickable.Yes,
            };
        }
        if (obj.isMissile()) {
            return this.missileColor_(obj);
        }
        // In FOV
        if (dotsManagerInstance.inViewData?.[obj.id] === 1 && dotsManagerInstance.inSunData[obj.id] > 0 && this.objectTypeFlags.sunlightFov) {
            if (dotsManagerInstance.inSunData[obj.id] === 0) {
                if (this.objectTypeFlags.satLow) {
                    return {
                        color: this.colorTheme.umbral,
                        pickable: Pickable.No,
                    };
                }
                return {
                    color: this.colorTheme.deselected,
                    pickable: Pickable.No,
                };
            }
            // TODO: Work out a system for vmag filtering
            return {
                color: this.colorTheme.sunlightInview,
                pickable: Pickable.Yes,
            };
        }
        // Not in FOV
        const sat = obj;
        if (!dotsManagerInstance.inViewData?.[sat.id]) {
            if (dotsManagerInstance.inSunData[sat.id] === SunStatus.SUN && this.objectTypeFlags.satHi) {
                if (sat.vmag !== null) {
                    if (sat.vmag < 3) {
                        return {
                            color: this.colorTheme.sunlight100,
                            pickable: Pickable.Yes,
                        };
                    }
                    if (sat.vmag <= 4.5) {
                        return {
                            color: this.colorTheme.sunlight80,
                            pickable: Pickable.Yes,
                        };
                    }
                    if (sat.vmag > 4.5) {
                        return {
                            color: this.colorTheme.sunlight60,
                            pickable: Pickable.Yes,
                        };
                    }
                }
                if (sat.isPayload()) {
                    return {
                        color: this.colorTheme.sunlight80,
                        pickable: Pickable.Yes,
                    };
                }
                if (sat.isRocketBody()) {
                    return {
                        color: this.colorTheme.sunlight100,
                        pickable: Pickable.Yes,
                    };
                }
                if (sat.isDebris()) {
                    return {
                        color: this.colorTheme.sunlight60,
                        pickable: Pickable.Yes,
                    };
                }
                return {
                    color: this.colorTheme.sunlight60,
                    pickable: Pickable.Yes,
                };
            }
            if (dotsManagerInstance.inSunData[sat.id] === SunStatus.PENUMBRAL && this.objectTypeFlags.satMed) {
                return {
                    color: this.colorTheme.penumbral,
                    pickable: Pickable.Yes,
                };
            }
            if (dotsManagerInstance.inSunData[sat.id] === SunStatus.UMBRAL && this.objectTypeFlags.satLow) {
                return {
                    color: this.colorTheme.umbral,
                    pickable: Pickable.No,
                };
            }
            // The color was deselected
            return {
                color: this.colorTheme.deselected,
                pickable: Pickable.No,
            };
        }
        // The color was deselected
        return {
            color: this.colorTheme.deselected,
            pickable: Pickable.No,
        };
    }
    static layersHtml = html `
  <ul id="layers-list-sunlight">
    <li>
      <div class="Square-Box layers-satLow-box"></div>
      In Umbral
    </li>
    <li>
      <div class="Square-Box layers-satMed-box"></div>
      In Penumbral
    </li>
    <li>
      <div class="Square-Box layers-satHi-box"></div>
      In Sunlight
    </li>
    <li>
      <div class="Square-Box layers-sunlightFov-box"></div>
      Satellite in FOV
    </li>
    <li>
      <div class="Square-Box layers-facility-box"></div>
      Launch Site
    </li>
  </ul>
  `.trim();
}
